import React, { useState } from 'react';
import { isTaskOnTime, TASK_STATUSES } from '../data/supabaseApi';
import './TaskTable.css';

const statusClassMap = {
  'To Do': 'status-todo',
  'In Development': 'status-in-dev',
  'In QA': 'status-qa',
  'Client Review': 'status-review',
  'Completed': 'status-completed',
  'Blocked': 'status-blocked',
  'Delayed': 'status-delayed'
};

const TaskTable = ({
  tasks,
  showMemberColumn = false,
  showAdminColumns = false,
  onEdit = null,
  onAdminUpdate = null,
  onDeleteTask = null
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [editRemarks, setEditRemarks] = useState({});
  const [editVerdict, setEditVerdict] = useState({});
  const [editStatus, setEditStatus] = useState({});
  const [editEstimatedTime, setEditEstimatedTime] = useState({});
  const [editActualTime, setEditActualTime] = useState({});

  const getStatusBadgeClass = (status) => statusClassMap[status] || 'status-default';

  const renderIndicator = (task) => {
    const onTime = isTaskOnTime(task);
    if (task.currentStatus === 'Delayed') {
      return <span className="indicator indicator-delayed" title="Delayed">Delayed</span>;
    }
    if (task.currentStatus === 'Completed') {
      if (onTime === true) return <span className="indicator indicator-ontrack" title="On track">On track</span>;
      if (onTime === false) return <span className="indicator indicator-delayed" title="Completed late">Late</span>;
    }
    return <span className="indicator indicator-pending" title="In progress">—</span>;
  };

  const handleSaveAdmin = (task) => {
    if (!onAdminUpdate) return;
    const payload = {
      adminRemarks: editRemarks[task.id] !== undefined ? editRemarks[task.id] : task.adminRemarks,
      adminVerdict: editVerdict[task.id] !== undefined ? editVerdict[task.id] : task.adminVerdict,
      currentStatus: editStatus[task.id] !== undefined ? editStatus[task.id] : (task.currentStatus || task.status),
      estimatedTime: editEstimatedTime[task.id] !== undefined ? editEstimatedTime[task.id] : task.estimatedTime,
      actualTime: editActualTime[task.id] !== undefined ? editActualTime[task.id] : task.actualTime
    };
    onAdminUpdate(task.id, payload);
    setEditRemarks(prev => ({ ...prev, [task.id]: undefined }));
    setEditVerdict(prev => ({ ...prev, [task.id]: undefined }));
    setEditStatus(prev => ({ ...prev, [task.id]: undefined }));
    setEditEstimatedTime(prev => ({ ...prev, [task.id]: undefined }));
    setEditActualTime(prev => ({ ...prev, [task.id]: undefined }));
    setExpandedId(null);
  };

  const handleDeleteTask = (task, e) => {
    e.stopPropagation();
    if (!onDeleteTask) return;
    if (window.confirm(`Delete task "${task.taskName || task.task}"? This cannot be undone.`)) {
      onDeleteTask(task.id);
    }
  };

  const displayMember = (task) => task.assignedMember || task.userName;

  const TaskRowContent = ({ task }) => (
    <>
      {showMemberColumn && <td>{displayMember(task)}</td>}
      <td className="task-name-cell">
        <strong>{task.taskName || task.task}</strong>
        {task.taskDescription && (
          <div className="task-desc-preview">{task.taskDescription.slice(0, 60)}{task.taskDescription.length > 60 ? '…' : ''}</div>
        )}
      </td>
      <td>{task.clientName}</td>
      <td>{task.projectName || task.project}</td>
      <td className="date-cell">{task.plannedStartDate || '—'}</td>
      <td className="date-cell">{task.plannedEndDate || '—'}</td>
      <td>
        <span className={`status-badge ${getStatusBadgeClass(task.currentStatus || task.status)}`}>
          {task.currentStatus || task.status}
        </span>
      </td>
      <td>
        {(task.estimatedTime != null && task.estimatedTime !== '') ? `${task.estimatedTime}h` : '—'}
        {(task.currentStatus === 'Completed' || task.actualTime != null) && (
          <> / {(task.actualTime != null && task.actualTime !== '') ? `${task.actualTime}h` : '—'}</>
        )}
      </td>
      <td>{renderIndicator(task)}</td>
      {showAdminColumns && (
        <td>
          {task.adminVerdict ? (
            <span className="verdict-badge verdict-set">{task.adminVerdict}</span>
          ) : (
            <span className="verdict-badge verdict-none">—</span>
          )}
          {task.delayReason && (
            <div className="delay-reason-preview" title={task.delayReason}>
              Delay: {task.delayReason.slice(0, 30)}{task.delayReason.length > 30 ? '…' : ''}
            </div>
          )}
        </td>
      )}
      {onEdit && (
        <td>
          <button type="button" className="edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(task); }}>Edit</button>
        </td>
      )}
    </>
  );

  return (
    <div className="task-table-container">
      <div className="task-table-wrapper">
      <table className="task-table">
        <thead>
          <tr>
            {showMemberColumn && <th>Member</th>}
            <th>Task</th>
            <th>Client</th>
            <th>Project</th>
            <th>Planned Start</th>
            <th>Planned End</th>
            <th>Status</th>
            <th>Est. vs Actual</th>
            <th>Timeline</th>
            {showAdminColumns && <th>Admin</th>}
            {onEdit && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td colSpan={9 + (showMemberColumn ? 1 : 0) + (showAdminColumns ? 1 : 0) + (onEdit ? 1 : 0)} className="no-tasks">
                No tasks found
              </td>
            </tr>
          ) : (
            tasks.map((task) => (
              <React.Fragment key={task.id}>
                <tr
                  className={expandedId === task.id ? 'expanded' : ''}
                  onClick={() => showAdminColumns && setExpandedId(expandedId === task.id ? null : task.id)}
                >
                  <TaskRowContent task={task} />
                </tr>
                {showAdminColumns && expandedId === task.id && (
                  <tr className="admin-detail-row">
                    <td colSpan={9 + (showMemberColumn ? 1 : 0) + 1 + (onEdit ? 1 : 0)}>
                      <div className="admin-detail-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-detail-section">
                          <label>Status</label>
                          <select
                            className="admin-verdict-select"
                            value={editStatus[task.id] !== undefined ? editStatus[task.id] : (task.currentStatus || task.status)}
                            onChange={(e) => setEditStatus(prev => ({ ...prev, [task.id]: e.target.value }))}
                          >
                            {TASK_STATUSES.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div className="admin-detail-section">
                          <label>Estimated (h)</label>
                          <input
                            type="number"
                            className="admin-remarks-input"
                            min="0"
                            step="0.5"
                            value={editEstimatedTime[task.id] !== undefined ? editEstimatedTime[task.id] : (task.estimatedTime ?? '')}
                            onChange={(e) => setEditEstimatedTime(prev => ({ ...prev, [task.id]: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                          />
                        </div>
                        <div className="admin-detail-section">
                          <label>Actual (h)</label>
                          <input
                            type="number"
                            className="admin-remarks-input"
                            min="0"
                            step="0.5"
                            value={editActualTime[task.id] !== undefined ? editActualTime[task.id] : (task.actualTime ?? '')}
                            onChange={(e) => setEditActualTime(prev => ({ ...prev, [task.id]: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                          />
                        </div>
                        <div className="admin-detail-section">
                          <label>Delay reason</label>
                          <p>{task.delayReason || '—'}</p>
                        </div>
                        <div className="admin-detail-section full-width">
                          <label>Admin remarks</label>
                          <textarea
                            className="admin-remarks-input"
                            value={editRemarks[task.id] !== undefined ? editRemarks[task.id] : (task.adminRemarks || '')}
                            onChange={(e) => setEditRemarks(prev => ({ ...prev, [task.id]: e.target.value }))}
                            placeholder="Add feedback or remarks"
                            rows={2}
                          />
                        </div>
                        <div className="admin-detail-section">
                          <label>Verdict</label>
                          <select
                            className="admin-verdict-select"
                            value={editVerdict[task.id] !== undefined ? editVerdict[task.id] : (task.adminVerdict || '')}
                            onChange={(e) => setEditVerdict(prev => ({ ...prev, [task.id]: e.target.value }))}
                          >
                            <option value="">—</option>
                            <option value="Accepted">Accepted</option>
                            <option value="Needs Rework">Needs Rework</option>
                            <option value="Escalated">Escalated</option>
                          </select>
                        </div>
                        <div className="admin-detail-actions">
                          <button type="button" className="save-admin-btn" onClick={() => handleSaveAdmin(task)}>
                            Save
                          </button>
                          {onDeleteTask && (
                            <button type="button" className="delete-task-btn" onClick={(e) => handleDeleteTask(task, e)}>
                              Delete task
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
      </div>

      <div className="task-cards">
        {tasks.length === 0 ? (
          <div className="no-tasks">No tasks found</div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className="task-card"
              onClick={() => onEdit && onEdit(task)}
            >
              <div className="task-card-header">
                <strong>{task.taskName || task.task}</strong>
                <span className={`status-badge ${getStatusBadgeClass(task.currentStatus || task.status)}`}>
                  {task.currentStatus || task.status}
                </span>
              </div>
              <div className="task-card-meta">
                {showMemberColumn && <span>{displayMember(task)}</span>}
                <span>{task.clientName}</span>
                <span>{task.projectName || task.project}</span>
              </div>
              <div className="task-card-dates">
                {task.plannedStartDate || '—'} → {task.plannedEndDate || '—'}
              </div>
              <div className="task-card-indicator">{renderIndicator(task)}</div>
              {onEdit && (
                <button type="button" className="edit-btn task-card-edit" onClick={(e) => { e.stopPropagation(); onEdit(task); }}>
                  Edit
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskTable;
